import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete, Box, Chip, TextField, Typography, Paper,
    CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './departmentsApi';
import type { Department, MainGroup } from './departmentsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    departmentId: number | null;
}

export function DepartmentView({ departmentId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<Department | null>(null);
    const [mainGroups, setMainGroups] = useState<MainGroup[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<Department | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const mgs = await api.getMainGroups();
            setMainGroups(mgs);
            if (departmentId === null) {
                setItem({ id: 0, departmentCode: '', departmentDescription: '', mainGroups: [] });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getDepartment(departmentId);
                data.mainGroups = data.mainGroups ?? [];
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [departmentId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isCodeValid = !!item.departmentCode;
    const isDescriptionValid = !!item.departmentDescription;
    const canSave = isCodeValid && isDescriptionValid;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = await api.saveDepartment(item);
            response.mainGroups = response.mainGroups ?? [];
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof Department>(field: K, value: Department[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.departmentCode ?? ''} ${item.departmentDescription ?? ''}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={canSave}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('department.departmentCode')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.departmentCode}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.departmentCode}
                                onChange={e => update('departmentCode', e.target.value)}
                                error={!isCodeValid}
                                helperText={!isCodeValid ? t('common.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('department.departmentDescription')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.departmentDescription}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.departmentDescription}
                                onChange={e => update('departmentDescription', e.target.value)}
                                error={!isDescriptionValid}
                                helperText={!isDescriptionValid ? t('common.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('department.mainGroups')}>
                        {!isEditing ? (
                            <Box>
                                {item.mainGroups.map(mg => (
                                    <Typography sx={sx14} key={mg.id}>
                                        {mg.mainGroupCode} {mg.mainGroupDescription}
                                    </Typography>
                                ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...mainGroups].sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))}
                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                value={item.mainGroups}
                                onChange={(_, value) => update('mainGroups', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((mg, i) => (
                                        <Chip {...getTagProps({ index: i })} key={mg.id}
                                            label={`${mg.mainGroupCode} ${mg.mainGroupDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
